import React, { useState, useContext } from 'react';
import './GroupManagement.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import GroupUpdateModal from '../GroupUpdateModal/GroupUpdateModal';
import GroupCreationModal from '../GroupCreationModal/GroupCreationModal';
import GroupRow from '../GroupRow/GroupRow';
import GroupDeletionModal from '../GroupDeletionModal/GroupDeletionModal';
import ListUpdateMessage from '../ListUpdateMessage/ListUpdateMessage';
import InteractiveList from '../InteractiveList/InteractiveList';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import { AdminContext } from '../../contexts/AdminContext';
import logComponent from '../../common/logComponent';

export default function GroupManagement({ addGroup: propsAddGroup, deleteGroup: propsDeleteGroup, updateGroup: propsUpdateGroup, groupsLoading, groupsError, reload  }) {
  const environmentContext = useContext(EnvironmentContext);
  const adminContext = useContext(AdminContext);
  const [visibleGroup, setVisibleGroup] = useState(null);
  const [creationModalIsVisible, setCreationModalIsVisible] = useState(false);
  const [deletionTarget, setDeletionTarget] = useState(null);
  const [addedGroup, setAddedGroup] = useState(null);
  const [deletedGroup, setDeletedGroup] = useState(null);
  const [updatedGroup, setUpdatedGroup] = useState(null);
  const [mostRecentGroupUpdate, setMostRecentGroupUpdate] = useState(null);

  const groupToDisplayInMessage = addedGroup || updatedGroup || deletedGroup;
  const groupName = groupToDisplayInMessage ? groupToDisplayInMessage.name : '';
  const updateType = addedGroup ? 'created' : (updatedGroup ? 'updated' : (deletedGroup ? 'deleted' : ''));

  const { activeUser: adminUser } = environmentContext;
  const { groups } = adminContext;

  if (logComponent('GroupManagement')) { console.debug('GroupManagement render'); }

  const addGroup = (group) => {
    if (logComponent('GroupManagement')) { console.debug('GroupManagement addGroup'); }
    propsAddGroup(group);

    setAddedGroup(group); 
    setUpdatedGroup(null);
    setDeletedGroup(null);
    setMostRecentGroupUpdate(Date.now());
   
  }

  const deleteGroup = (group) => {
    if (logComponent('GroupManagement')) { console.debug('GroupManagement addGroup'); }
    propsDeleteGroup(group);
    setAddedGroup(null);
    setUpdatedGroup(null);
    setDeletedGroup(group);
    setMostRecentGroupUpdate(Date.now());
  }

  const updateGroup = (updatedGroup) => {
    if (logComponent('GroupManagement')) { console.debug('GroupManagement addGroup'); }
    propsUpdateGroup(updatedGroup);
    setAddedGroup(null);
    setUpdatedGroup(updatedGroup);
    setDeletedGroup(null);
    setMostRecentGroupUpdate(Date.now());
  }

  const showGroupCreationModal = () => {
    if (logComponent('GroupManagement')) { console.debug('GroupManagement addGroup'); }
    setVisibleGroup(null);
    setCreationModalIsVisible(true);
  }

  const hideGroupCreationModal = () => {
    if (logComponent('GroupManagement')) { console.debug('GroupManagement addGroup'); }
    setCreationModalIsVisible(false);
  }  

  const showGroupDeletionModal = (group) => {
    if (logComponent('GroupManagement')) { console.debug('GroupManagement addGroup'); }
    setDeletionTarget(group);
  }

  const hideGroupDeletionModal = () => {
    if (logComponent('GroupManagement')) { console.debug('GroupManagement addGroup'); }
    setDeletionTarget(null);
  }

  const hideVisibleGroup = () => {
    if (logComponent('GroupManagement')) { console.debug('GroupManagement addGroup'); }
    setVisibleGroup(null);
  }  

  const showDetail = (group, method) => {
    if (logComponent('GroupManagement')) { console.debug('GroupManagement addGroup'); }
    if (method === 'detail') {
      toggleVisibleGroup(group);
    } else if (method === 'delete') {
      showGroupDeletionModal(group);
    }
  }

  const toggleVisibleGroup = (groupToToggle) => {
    if (logComponent('GroupManagement')) { console.debug('GroupManagement addGroup'); }
    let newVisibleGroup = null;

    if (visibleGroup !== groupToToggle) {
      newVisibleGroup = groupToToggle;
    }
    setVisibleGroup(newVisibleGroup);
  }  

  const getColumns = () => {
    if (logComponent('GroupManagement')) { console.debug('GroupManagement getColumns'); }

    if (adminUser.groupTypeString === 'Status Solutions' || adminUser.groupTypeString === 'Corporate') {
      return [
        { name: 'Name', sortField: 'Name' },
        { name: 'Mimic', hideAt: 'sm', style: { width: '30%' } },
        { name: '', style: { width: '64px' } }
      ];
    } else if (adminUser.groupTypeString === 'Facility') {
      // For facility admins
      return [
        { name: 'Name', sortField: 'Name' },
        { name: 'Correspondence', hideAt: 'sm', style: { width: '30%' } },
        { name: '', style: { width: '64px' } }
      ];
    } else {
      return [];
    }
  }

  const getFilters = () => {
    if (logComponent('GroupManagement')) { console.debug('GroupManagement getFilters'); }

    if (adminUser.groupTypeString === 'Facility') {
      // For facility admins
      return [
        {
          title: 'Correspondence',
          type: 'checkbox',
          name: 'corresondence',
          options: [{
            text: 'Yes',
            criteria: [{
              field: 'CanCorrespond',
              acceptedValues: [true]
            }]
          }, {
            text: 'No',
            criteria: [{
              field: 'CanCorrespond',
              acceptedValues: [false]
            }]
          }]
        }];
    } else {
      return null;
    }
  }

  return (
    <div className="Group-Management">
      {
        groupToDisplayInMessage &&
        <ListUpdateMessage
          message={`${groupName} has been successfully ${updateType}`}
          lastUpdate={mostRecentGroupUpdate} />
      }

      <InteractiveList
        columns={getColumns()}
        singularItemTerm="group"
        pluralItemTerm="groups"
        searchTextFields={['name']}
        searchPlaceholder="Search groups"
        defaultSortField="Name"
        filters={getFilters()}
        items={groups}
        itemsLoading={groupsLoading}
        itemsError={groupsError}
        rowComponent={GroupRow}
        showDetail={showDetail}
        keyField="groupId"
        reloadAction={reload} >
        {
          adminUser && adminUser.groupTypeString != 'Family' &&
          groups &&
          <button
            className="Group-Management__add-button"
            onClick={showGroupCreationModal}>
            <FontAwesomeIcon
              className="Group-Management__add-button-icon"
              icon="plus" />Add new group
          </button>
        }
      </InteractiveList>

      {
        visibleGroup &&
        <GroupUpdateModal
          adminUser={adminUser}
          updateGroup={updateGroup}
          hide={hideVisibleGroup}
          groups={groups}
          group={visibleGroup} />
      }
      {
        creationModalIsVisible &&
        <GroupCreationModal
          adminUser={adminUser}
          groups={groups}
          addGroup={addGroup}
          isVisible={creationModalIsVisible}
          hide={hideGroupCreationModal} />
      }
      {
        !!deletionTarget &&
        <GroupDeletionModal
          group={deletionTarget}
          deleteGroup={deleteGroup}
          hide={hideGroupDeletionModal} />
      }
    </div>
  );
}